import {
  ConditionExpression,
  DynamicString,
} from "../../models";

export type DynamicStringConditionBranch = {
  condition: ConditionExpression;
  text: string;
};

export type ParsedDynamicString = {
  branches: DynamicStringConditionBranch[];
  defaultText: string;
};

function isDynamicStringRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isDynamicStringValue(value: unknown): value is DynamicString {
  return isDynamicStringRecord(value);
}

export function parseDynamicString(value: unknown): ParsedDynamicString | null {
  if (!isDynamicStringRecord(value)) {
    return null;
  }

  const entries = Object.entries(value);
  const defaultEntries = entries.filter(([, branchValue]) => branchValue === "default");

  if (defaultEntries.length !== 1) {
    return null;
  }

  return {
    defaultText: defaultEntries[0][0],
    branches: entries
      .filter(([, branchValue]) => branchValue !== "default")
      .map(([text, condition]) => ({
        text,
        condition: condition as ConditionExpression,
      })),
  };
}

export function getDynamicStringDefaultText(
  value: string | DynamicString | undefined,
): string | null {
  if (typeof value === "string") {
    return value;
  }

  return parseDynamicString(value)?.defaultText ?? null;
}

export function serializeDynamicString(
  defaultText: string,
  branches: DynamicStringConditionBranch[],
): DynamicString {
  const nextValue: DynamicString = {
    [defaultText]: "default",
  };

  branches.forEach(({ condition, text }) => {
    nextValue[text] = condition;
  });

  return nextValue;
}

export function getDuplicateDynamicStringTexts(
  defaultText: string,
  branches: Array<{ text: string }>,
): Set<string> {
  const counts = new Map<string, number>();

  [defaultText, ...branches.map((branch) => branch.text)].forEach((text) => {
    counts.set(text, (counts.get(text) ?? 0) + 1);
  });

  return new Set(
    Array.from(counts.entries())
      .filter(([, count]) => count > 1)
      .map(([text]) => text),
  );
}

export function getNextDynamicStringBranchText(
  defaultText: string,
  existingTexts: string[],
): string {
  const usedTexts = new Set([defaultText, ...existingTexts]);

  if (!usedTexts.has("")) {
    return "";
  }

  let suffix = 1;
  let candidate = "Alternative value";

  while (usedTexts.has(candidate)) {
    suffix += 1;
    candidate = `Alternative value ${suffix}`;
  }

  return candidate;
}

export function createDynamicStringFromStatic(
  staticValue: string | undefined,
  condition: ConditionExpression,
): DynamicString {
  const defaultText = staticValue ?? "";

  return serializeDynamicString(defaultText, [
    {
      text: getNextDynamicStringBranchText(defaultText, []),
      condition,
    },
  ]);
}
